import React, { useState, useContext } from 'react'
import { Form, Col } from 'react-bootstrap'
import constants from '../../../config/constants'
import { toPersonName, isValidPersonName } from '../../../utility/stringUtils'
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input'
import { Formik } from 'formik'
import * as yup from 'yup'
import { MigrantContext } from '../../../contexts/migrantContext'

interface EditProfileFormProps {
  editProfile: (
    firstName: string,
    lastName: string,
    phonenumber?: string,
    avatarFile?: File | null,
    password?: string,
    newPassword?: string,
    email?: string
  ) => void
  isSocial: boolean
}

const errorMsg = constants.inlineError
const schema = yup.object({
  firstName: yup
    .string()
    .required(errorMsg.fieldRequired.replace('%s', 'First name'))
    .test('ValidPersonName', errorMsg.auth.invalidFirstName, value => {
      return isValidPersonName(value)
    }),
  lastName: yup
    .string()
    .required(errorMsg.fieldRequired.replace('%s', 'Last name'))
    .test('ValidPersonName', errorMsg.auth.invalidLastName, value => {
      return isValidPersonName(value)
    }),
  phonenumber: yup.string(),
  password: yup
    .string()
    .min(constants.passwordMinLength, errorMsg.auth.invalidPassword),
  newPassword: yup
    .string()
    .min(constants.passwordMinLength, errorMsg.auth.invalidPassword),
  avatar: yup.mixed(),
  email: yup.string().email()
})

const EditProfileForm: React.FC<EditProfileFormProps> = ({
  editProfile,
  isSocial
}) => {
  const [isValidNumber, setIsValidNumber] = useState(true)
  const migrantContext = useContext(MigrantContext)
  const [avatar, setAvatar] = useState<File | null>(null!)
  return (
    <Formik
      data-testid="edit-form"
      validationSchema={schema}
      onSubmit={values => {
        isSocial
          ? editProfile(
              values.firstName,
              values.lastName,
              values.phonenumber,
              avatar,
              values.email
            )
          : editProfile(
              values.firstName,
              values.lastName,
              values.phonenumber,
              avatar,
              values.password,
              values.newPassword,
              values.email
            )
      }}
      initialValues={{
        firstName: migrantContext?.profile?.firstName!,
        lastName: migrantContext?.profile?.lastName!,
        phonenumber: migrantContext?.profile?.phonenumber!,
        email: migrantContext?.profile?.email!,
        password: '',
        newPassword: ''
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label className="mochi-sign-in-label">
              Upload Avatar
            </Form.Label>
            <Form.File id="formcheck-api-regular">
              {/* TODO: Change depending to the mockup */}
              <Form.File.Input
                className="pb-lg-3"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAvatar(e.target.files![0])
                }}
              />
            </Form.File>
          </Form.Group>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label
                className="mochi-sign-in-label"
                htmlFor="editprofile-first-name"
              >
                First Name
              </Form.Label>
              <Form.Control
                id="editprofile-first-name"
                type="text"
                name="firstName"
                value={values.firstName}
                onBlur={handleBlur}
                isInvalid={touched.firstName && !!errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const fName = toPersonName(e.target.value)
                  setFieldValue('firstName', fName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label
                className="mochi-sign-in-label"
                htmlFor="editprofile-last-name"
              >
                Last Name
              </Form.Label>
              <Form.Control
                id="editprofile-last-name"
                type="text"
                name="lastName"
                value={values.lastName}
                onBlur={handleBlur}
                isInvalid={touched.lastName && !!errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const lName = toPersonName(e.target.value)
                  setFieldValue('lastName', lName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Group>
            <Form.Label
              className="mochi-sign-in-label"
              htmlFor="editprofile-email"
            >
              Email
            </Form.Label>
            <Form.Control
              id="editprofile-email"
              data-testid="editprofile-email"
              type="email"
              name="email"
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
              isInvalid={touched.email && !!errors.email}
              disabled={isSocial}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label
              className="mochi-sign-in-label"
              htmlFor="editprofile-phonenumber"
            >
              Phone number
            </Form.Label>
            <PhoneInput
              id="edit-phonenumber"
              data-testid="editprofile-phone"
              international
              value={values.phonenumber}
              className="form-control"
              onChange={number => {
                setFieldValue('phonenumber', number)
                setFieldTouched('phonenumber')
                setIsValidNumber(isPossiblePhoneNumber(number))
              }}
              disabled={!isSocial}
            />
            {touched.phonenumber && !isValidNumber && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                {errorMsg.auth.invalidPhonenumber}
              </div>
            )}
          </Form.Group>
          {/*
          {isSocial ? null : (
            <>
              <Form.Group data-testid="pass-login">
                <Form.Label
                  className="mochi-sign-in-label"
                  htmlFor="editprofile-password"
                >
                  Current Password
                </Form.Label>
                <Form.Control
                  id="editprofile-password"
                  type="password"
                  name="password"
                  placeholder="Enter password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.password && !!errors.password}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group>
                <Form.Label
                  className="mochi-sign-in-label"
                  htmlFor="editprofile-password"
                >
                  New Password
                </Form.Label>
                <Form.Control
                  id="editprofile-newpassword"
                  type="password"
                  name="newPassword"
                  placeholder="Enter password"
                  value={values.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.newPassword && !!errors.newPassword}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.newPassword}
                </Form.Control.Feedback>
              </Form.Group>
            </>
          )}

           */}
          <div className="text-center mt-4">
            <button
              className="mochi-button px-3 py-2 "
              type="submit"
              data-testid="editprofile-submit-button"
            >
              Save Changes
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default EditProfileForm
